/**
 * DemoMode -- context + badge for marking synthetic (demo) data in the UI.
 *
 * When an organization runs in demo mode (`organizations.is_demo_mode = 1`),
 * every analytics/SEO/ads surface renders realistic SYNTHETIC data. This
 * module makes that provenance impossible to miss:
 *
 *   - `DemoModeProvider` — wrap any subtree served by a demo org.
 *   - `useDemoMode()`    — read the flag anywhere below the provider.
 *   - `DemoBadge`        — a small, high-contrast "Sample data" pill that
 *                          `MetricCard` / `ChartCard` render automatically
 *                          when the context flag is true (no prop threading).
 *
 * The default context value is `false`, so every existing consumer that does
 * NOT wrap a provider is completely unaffected (additive, zero blast radius).
 *
 * @layer atom
 * @package @plerity/ui-core
 */

import { createContext, memo, useContext } from "react";
import { cn } from "../utils/cn";

const DemoModeContext = createContext<boolean>(false);

export interface DemoModeProviderProps {
  /** True when the surrounding org is a demo org (all data is synthetic). */
  value: boolean;
  children: React.ReactNode;
}

/** Marks a subtree as rendering demo data. Cards below render a Sample badge. */
export function DemoModeProvider({ value, children }: DemoModeProviderProps): React.ReactElement {
  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
}

/** True when the nearest DemoModeProvider marks this subtree as demo. */
export function useDemoMode(): boolean {
  return useContext(DemoModeContext);
}

export interface DemoBadgeProps {
  className?: string;
  /** Short label — defaults to "Sample". */
  label?: string;
}

/**
 * Small amber "Sample data" pill. Uses the always-on warning ramp with
 * explicit dark: variants so it stays legible in both themes and reads as
 * meta-chrome, not real content. `title` exposes the full explanation.
 */
export const DemoBadge = memo(function DemoBadge({ className, label = "Sample" }: DemoBadgeProps) {
  return (
    <span
      role="note"
      title="Sample data — for demonstration only. Not real performance."
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
        "border-amber-300 bg-amber-100 text-amber-800",
        "dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-300",
        className
      )}
    >
      <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2 1 21h22L12 2zm0 5 7.5 13h-15L12 7zm-1 4v4h2v-4h-2zm0 5v2h2v-2h-2z" />
      </svg>
      {label}
    </span>
  );
});

DemoBadge.displayName = "DemoBadge";

export interface LiveBadgeProps {
  className?: string;
  /** Short label — defaults to "Live". */
  label?: string;
}

/**
 * Small emerald "Live data" pill — the reassuring counterpart to `DemoBadge`.
 * Rendered on surfaces of a demo org that are backed by REAL connected data
 * (e.g. a linked website's genuine GA4 property), so a demo account's real
 * surfaces are visibly distinguished from its sample-data ones. Uses the
 * always-on success ramp with explicit dark: variants for legibility in both
 * themes. `title` exposes the full explanation.
 */
export const LiveBadge = memo(function LiveBadge({ className, label = "Live" }: LiveBadgeProps) {
  return (
    <span
      role="note"
      title="Live data — reflects your real, connected data."
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
        "border-emerald-300 bg-emerald-100 text-emerald-800",
        "dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300",
        className
      )}
    >
      <svg
        width="9"
        height="9"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M20 6 9 17l-5-5" />
      </svg>
      {label}
    </span>
  );
});

LiveBadge.displayName = "LiveBadge";
